import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  functionalUpdate,
  getExpandedRowModel,
  RowData,
  TableOptions,
} from '@tanstack/react-table';
import * as styles from './Table.css';
import { Button, Progress } from '@instamenu/ui-system-next';
import { useLocale } from '@instamenu/context-i18n';
import { SortingState } from '@tanstack/react-table';
import { clsx } from 'clsx';

type PaginationType = {
  pageIndex: number;
  pageInfo: any;
  pageSize?: number;
  totalCount: number;
  movePage: (direction: -1 | 1) => void;
};

type TableProps<T extends RowData> = {
  data?: T[];
  onRowClick?: (row: T) => void;
  onSortingChange?: (sorting: SortingState) => void;
  loading?: boolean;
  fetchMore?: () => void;
  orderBy?: string;
  sorting?: SortingState;
  initialState?: any;
  getSubRows?: (row: T) => any[];
} & Pick<TableOptions<T>, 'columns'> &
  PaginationType;

const empty: never[] = [];

export function PagedConnectionTable<T extends object>({
  data,
  columns,
  getSubRows = () => [],
  onRowClick,
  onSortingChange = () => undefined,
  loading,
  pageSize = 10,
  pageInfo,
  pageIndex,
  movePage,
  totalCount,
  sorting = [],
  initialState,
}: TableProps<T>) {
  const table = useReactTable({
    data: data ?? empty,
    columns: columns,
    initialState: {
      ...initialState,
    },
    state: {
      sorting,
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualSorting: true,
    manualPagination: true,
    onSortingChange: (input) => onSortingChange(functionalUpdate(input, sorting)),
    getSubRows,
    getExpandedRowModel: getExpandedRowModel(),
  });

  return (
    <div style={{ position: 'relative' }}>
      <div className={styles.scrollWrap}>
        <table className={styles.table}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    className={header.column.columnDef.meta?.style}
                    style={{
                      width: header.column.columnDef.size,
                      cursor: header.column.getCanSort() ? 'pointer' : 'default',
                    }}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort() && (
                      <span
                        className={clsx([
                          styles.arrows,
                          header.column.getIsSorted() === 'asc' ? styles.ascending : false,
                          header.column.getIsSorted() === 'desc' ? styles.descending : false,
                        ])}
                      />
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className={onRowClick ? styles.clickableRow : undefined}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={cell.column.columnDef.meta?.style}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        pageInfo={pageInfo}
        totalCount={totalCount}
        pageSize={pageSize}
        pageIndex={pageIndex}
        movePage={movePage}
      />
      {loading && (
        <div className={styles.loading}>
          <Progress size={32} />
        </div>
      )}
    </div>
  );
}

const Pagination = ({ pageInfo, totalCount, pageSize, pageIndex, movePage }: PaginationType) => {
  const { i18n } = useLocale();
  if (!pageSize) return null;
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: 16,
        marginBottom: 16,
      }}
    >
      <div>
        {i18n.t('pagedConnectionTable.pagination', {
          curr: pageIndex * pageSize + 1,
          all: Math.min(totalCount, (pageIndex + 1) * pageSize),
          total: totalCount,
        })}
      </div>
      <div style={{ display: 'flex', gap: 16 }}>
        <Button
          variant="secondary"
          size="xs"
          disabled={!pageInfo.hasPreviousPage}
          onClick={() => movePage(-1)}
        >
          {i18n.t('pagedConnectionTable.previous')}
        </Button>
        <Button
          variant="secondary"
          size="xs"
          disabled={!pageInfo.hasNextPage}
          onClick={() => movePage(1)}
        >
          {i18n.t('pagedConnectionTable.next')}
        </Button>
      </div>
    </div>
  );
};
